import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * useBottomSheet - Provides swipe-to-close gesture handling and scroll position tracking
 * for mobile bottom-sheet modals.
 *
 * @param {Function} onClose - Callback to close the sheet
 * @param {Object} options
 * @param {number} options.swipeThreshold - px dragged down before dismissing (default 120)
 * @param {number} options.velocityThreshold - px/ms velocity to dismiss (default 0.5)
 */
export function useBottomSheet(onClose, { swipeThreshold = 120, velocityThreshold = 0.5 } = {}) {
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartTime = useRef(null);
  const currentTranslate = useRef(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // ── Touch handlers ──
  const handleTouchStart = useCallback((e) => {
    dragStartY.current = e?.touches?.[0]?.clientY;
    dragStartTime.current = Date.now();
    currentTranslate.current = 0;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (dragStartY?.current === null) return;
    const delta = e?.touches?.[0]?.clientY - dragStartY?.current;
    if (delta < 0) return; // prevent dragging up
    currentTranslate.current = delta;
    setTranslateY(delta);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (dragStartY?.current === null) return;
    const elapsed = Date.now() - dragStartTime?.current;
    const velocity = currentTranslate?.current / elapsed;

    if (currentTranslate?.current > swipeThreshold || velocity > velocityThreshold) {
      // Animate out then close
      setTranslateY(window.innerHeight);
      setTimeout(() => {
        setTranslateY(0);
        onClose?.();
      }, 280);
    } else {
      // Snap back
      setTranslateY(0);
    }

    dragStartY.current = null;
    dragStartTime.current = null;
    currentTranslate.current = 0;
    setIsDragging(false);
  }, [onClose, swipeThreshold, velocityThreshold]);

  // ── Scroll shadow tracking ──
  const scrollRef = useRef(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const updateScrollShadows = useCallback(() => {
    const el = scrollRef?.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setShowTopShadow(scrollTop > 8);
    setShowBottomShadow(scrollTop + clientHeight < scrollHeight - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return;
    updateScrollShadows();
    el?.addEventListener('scroll', updateScrollShadows, { passive: true });
    return () => el?.removeEventListener('scroll', updateScrollShadows);
  }, [updateScrollShadows]);

  // Re-check shadows when content changes (e.g. after data loads)
  const recheckShadows = useCallback(() => {
    setTimeout(updateScrollShadows, 100);
  }, [updateScrollShadows]);

  const sheetStyle = {
    transform: `translateY(${translateY}px)`,
    transition: isDragging ? 'none' : 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1)',
    willChange: 'transform',
  };

  return {
    sheetRef,
    sheetStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    scrollRef,
    showTopShadow,
    showBottomShadow,
    recheckShadows,
  };
}
